import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

import {SetOrderByType} from 'shared/ui/Table/Table';
import IOrderBy, {DirectionType} from 'shared/model/IOrderBy';

export interface IUseSortOptions {
    defaultSort?: IOrderBy,
    sortFields?: readonly string[],
    resetPage?: boolean,
}

const toDirection = (value: string | null, fallback: DirectionType): DirectionType =>
    value === 'asc' || value === 'desc' ? value : fallback;

export const useSort = (
    {defaultSort, sortFields, resetPage = true}: IUseSortOptions = {},
): [IOrderBy | undefined, SetOrderByType] => {
    const [searchParams, setSearchParams] = useSearchParams();

    const order = searchParams.get('order');
    const dir = searchParams.get('dir');
    const defaultField = defaultSort?.field;
    const defaultDirection = defaultSort?.direction ?? 'desc';
    const sort = useMemo<IOrderBy | undefined>(() => {
        if (order && (!sortFields || sortFields.includes(order))) {
            return {field: order, direction: toDirection(dir, defaultDirection)};
        }
        return defaultField ? {field: defaultField, direction: defaultDirection} : undefined;
    }, [order, dir, sortFields, defaultField, defaultDirection]);

    const setSort: SetOrderByType = useCallback(value => {
        const nextSort = typeof value === 'function' ? value(sort) : value;
        const next = new URLSearchParams(searchParams);
        next.delete('order');
        next.delete('dir');
        if (nextSort?.field && nextSort.direction) {
            next.set('order', nextSort.field);
            next.set('dir', nextSort.direction);
        }
        if (resetPage) {
            next.set('page', '1');
        }
        setSearchParams(next);
    }, [sort, searchParams, setSearchParams, resetPage]);

    return [sort, setSort];
};
